import { FC, useEffect, useMemo, useState } from 'react';
import { Spin, Alert } from 'antd';
import { useGetForecastingForFysQuery } from '@/api/forecasting';
import type { DashboardFilters, KpiLens } from './forecasting.types';
import {
  computeTotals,
  filterRows,
  filterRowsByMonthKey,
  formatFyLabel,
  getDefaultDateFilters,
  groupByChannel,
  groupByMonthAndStage,
  metricFromLens,
  uniqueFyOptions
} from './utils/forecastingAggregations';
import { fyCodesInRange } from './utils/fyCalendar';
import { uncountedStatuses } from './utils/filterHelpers';
import { isDynamicForecastStage } from './utils/dynamicForecastMetrics';
import { FilterSidebar } from './components/FilterSidebar';
import { KpiScorecards } from './components/KpiScorecards';
import { MonthlyStackedChart } from './components/MonthlyStackedChart';
import { ChannelMixList } from './components/ChannelMixList';
import { BuyingPointTable } from './components/BuyingPointTable';
import {
  ActiveLensHint,
  WideChartRow,
  HeaderLogo,
  HeaderTitle,
  Layout,
  Main,
  Page,
  PageHeader
} from './Forecasting.styles';
import { LOGO_INK, LOGO_WHITE } from '@/assets/brand';
import { useThemeMode } from '@/styles';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

const LENS_LABELS: Record<KpiLens, string> = {
  dynamicForecastRevenue: 'Dynamic Forecast — Revenue',
  dynamicForecastGp: 'Dynamic Forecast — Gross Profit',
  budgetRevenue: 'Budget — Revenue',
  budgetGp: 'Budget — Gross Profit',
  /* The Budgetv2 figure is "Budget", and the forecast compared with it is
     "vs Budget" — it was "vs Target", which read as a different number from
     the Budget shown beside it. The same on the quarterly page. */
  vsBudgetRevenue: 'vs Budget — Revenue',
  vsBudgetGp: 'vs Budget — Gross Profit'
};

/** Monthly stacked chart uses the active KPI lens (vs Budget still shows dynamic forecast stages). */
function monthlyChartLens(lens: KpiLens): KpiLens {
  if (lens === 'vsBudgetRevenue') return 'dynamicForecastRevenue';
  if (lens === 'vsBudgetGp') return 'dynamicForecastGp';
  return lens;
}

const Forecasting: FC = () => {
  const { mode: themeMode } = useThemeMode();
  const defaultFilters = useMemo(() => getDefaultDateFilters(), []);
  const [filters, setFilters] = useState<DashboardFilters>(() => defaultFilters);
  const [activeLens, setActiveLens] = useState<KpiLens>('dynamicForecastRevenue');
  const [selectedMonthKey, setSelectedMonthKey] = useState<string | null>(null);

  /* Load every FY the date range reaches, not only the selected one: a range
     picked back into last year showed nothing, because only this FY's rows
     were ever fetched. */
  const fys = useMemo(
    () => fyCodesInRange(filters.dateFrom, filters.dateTo),
    [filters.dateFrom, filters.dateTo]
  );

  const { data: allRows = [], isFetching, isError } = useGetForecastingForFysQuery(
    { fys },
    { skip: !fys.length }
  );

  const fyOptions = useMemo(() => uniqueFyOptions(allRows), [allRows]);

  const filteredRows = useMemo(() => filterRows(allRows, filters), [allRows, filters]);

  const totals = useMemo(() => computeTotals(filteredRows), [filteredRows]);
  const statusesNotCounted = uncountedStatuses(filters.status);

  const activeMetric = metricFromLens(activeLens);
  const metricLabel = activeMetric === 'revenue' ? 'Revenue' : 'Gross Profit';
  const chartLens = monthlyChartLens(activeLens);

  useEffect(() => {
    setSelectedMonthKey(null);
  }, [activeLens, filters]);

  const monthlyData = useMemo(
    () =>
      groupByMonthAndStage(
        filteredRows,
        activeMetric,
        filters.dateFrom,
        filters.dateTo,
        chartLens
      ),
    [filteredRows, activeMetric, filters.dateFrom, filters.dateTo, chartLens]
  );

  const selectedMonthLabel = useMemo(() => {
    if (!selectedMonthKey) return null;
    return monthlyData.find((m) => m.monthKey === selectedMonthKey)?.axisLabel ?? selectedMonthKey;
  }, [monthlyData, selectedMonthKey]);

  const breakdownRows = useMemo(() => {
    if (!selectedMonthKey) return filteredRows;
    return filterRowsByMonthKey(filteredRows, selectedMonthKey);
  }, [filteredRows, selectedMonthKey]);

  const channelData = useMemo(
    () => groupByChannel(breakdownRows, activeMetric, activeLens),
    [breakdownRows, activeMetric, activeLens]
  );

  const dynamicForecastRowCount = useMemo(
    () => filteredRows.filter((r) => isDynamicForecastStage(r.Forecast_Stage)).length,
    [filteredRows]
  );

  const handleMonthSelect = (monthKey: string | null) => {
    setSelectedMonthKey(monthKey);
  };

  const handleFilterChange = (patch: Partial<DashboardFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      Object.keys(patch).forEach((k) => {
        if (patch[k as keyof DashboardFilters] === undefined) {
          delete next[k as keyof DashboardFilters];
        }
      });
      return next;
    });
  };

  const handleClearAllFilters = () => {
    setFilters(defaultFilters);
  };

  return (
    <Page>
      <PageHeader>
        <HeaderLogo src={themeMode === 'dark' ? LOGO_WHITE : LOGO_INK} alt="Skyrise" />
        <HeaderTitle>Financial Year Forecast</HeaderTitle>
      </PageHeader>

      {isError && (
        <Alert
          type="error"
          message="Failed to load forecasting data"
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {!isFetching && !isError && allRows.length === 0 && (
        <Alert
          type="warning"
          message={`No forecasting data returned for ${formatFyLabel(filters.fy)}`}
          description="Check that the forecasting endpoint is running, returns all rows for this FY (no row cap), and your account has access."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {!isFetching && allRows.length > 0 && filteredRows.length === 0 && (
        <Alert
          type="info"
          message={`No rows for ${formatFyLabel(filters.fy)} (${filters.dateFrom} – ${filters.dateTo}) with the current filters`}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {statusesNotCounted.length > 0 && (
        <Alert
          type="info"
          message={`${statusesNotCounted.join(', ')} ${
            statusesNotCounted.length > 1 ? "aren't" : "isn't"
          } counted in the Dynamic Forecast, Booked, Pipe or Budget figures`}
          description="Those deals add nothing to this page. The Current Quarter page lists them under Pre-Pipe in its campaign detail."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Spin spinning={isFetching}>
        <Layout>
          <FilterSidebar
            filters={filters}
            allRows={allRows}
            fyOptions={fyOptions}
            defaultDateRange={defaultFilters}
            onChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
          />

          <Main>
            <KpiScorecards totals={totals} activeLens={activeLens} onSelect={setActiveLens} />

            <ActiveLensHint>
              Showing breakdown for: <strong>{LENS_LABELS[activeLens]}</strong>
              {selectedMonthLabel && (
                <>
                  {' · '}
                  Month: <strong>{selectedMonthLabel}</strong>
                  {' '}
                  <button
                    type="button"
                    onClick={() => setSelectedMonthKey(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: `rgba(${APP_SHELL_THEME.overlayRgb}, 0.55)`,
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: 0,
                      textDecoration: 'underline'
                    }}
                  >
                    Clear
                  </button>
                </>
              )}
              {' · '}
              {breakdownRows.length.toLocaleString()} rows
              {!selectedMonthKey && (
                <>
                  {' in FY · '}
                  {dynamicForecastRowCount.toLocaleString()} with Forecast Stage = Dynamic Forecast
                </>
              )}
            </ActiveLensHint>

            <WideChartRow>
              <MonthlyStackedChart
                data={monthlyData}
                chartTitle={LENS_LABELS[activeLens]}
                selectedMonthKey={selectedMonthKey}
                onMonthSelect={handleMonthSelect}
                variant={
                  chartLens === 'budgetRevenue' || chartLens === 'budgetGp' ? 'budget' : 'dynamic'
                }
              />
              <ChannelMixList data={channelData} metricLabel={metricLabel} />
            </WideChartRow>

            <BuyingPointTable
              key={`${filters.fy}-${activeLens}-${selectedMonthKey ?? 'all'}`}
              rows={breakdownRows}
              lens={activeLens}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </Main>
        </Layout>
      </Spin>
    </Page>
  );
};

export default Forecasting;
